import classNames from 'classnames'
import { useMemo } from 'react'

import { SparklesIcon } from '@heroicons/react/24/solid'
import { useTranslation } from 'react-i18next'
import { Button } from '../common/Button'
import { ChartTypePicker } from './ChartTypePicker'
import { useCustomChartContext } from './CustomChartState'
import { DataTypePicker, DataTypePickerPreview } from './DataTypePicker'
import { ItemPicker, ItemPickerPreview } from './ItemPicker'
import { Step } from './types'

export const CurrentStep = () => {
  const { state, dispatch } = useCustomChartContext()
  const { t } = useTranslation()

  const steps: Step[] = useMemo(
    () => [
      {
        id: 1,
        name: t('custom_widgets.step_1'),
        component: <DataTypePicker />,
        preview: <DataTypePickerPreview />,
      },
      {
        id: 2,
        name: t('custom_widgets.step_2'),
        component: <ItemPicker />,
        preview: <ItemPickerPreview />,
      },
      {
        id: 3,
        name: t('custom_widgets.step_3'),
        component: <ChartTypePicker />,
      },
    ],
    [t]
  )

  const step = useMemo(() => steps[state.current] ?? steps[0], [state, steps])
  const progress = useMemo(
    () => ((state.current + 1) * 100) / steps.length,
    [state, steps]
  )

  return (
    <>
      <div className="absolute left-0 top-0 right-0 h-1.5 bg-gray-100">
        <div
          className="h-1.5 bg-indigo-700 transition-all rounded-br"
          style={{ width: `${progress}%` }}
        />
      </div>
      <div className="flex justify-between gap-8 flex-1 pt-2">
        <div className="flex flex-col justify-between py-5 w-[275px] h-full">
          <div className="flex flex-col gap-6 flex-1">
            <div className="flex gap-2 items-center">
              <h2 className="text-lg font-medium">
                {t('custom_widgets.new_widget')}
              </h2>
              <SparklesIcon className="h-4 text-green-500" />
            </div>
            <div className="space-y-2">
              {steps.map((s, i) => (
                <div
                  key={s.id}
                  className={classNames(
                    'text-left text-sm space-y-1 py-4 px-3 rounded-lg border transition-all w-full',
                    {
                      'font-medium text-black border-indigo-700 ring-1 ring-indigo-700 shadow-sm bg-white':
                        i === state.current,
                      'text-gray-600 border-transparent': i !== state.current,
                    }
                  )}
                >
                  <div>
                    {i + 1}. {s.name}
                  </div>
                  {s.preview}
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-start">
            <Button secondary onClick={() => dispatch({ type: 'RESET' })}>
              {t('cancel')}
            </Button>
          </div>
        </div>
        <div className="flex-1 flex flex-col justify-between space-y-4 bg-white rounded-lg border shadow px-6 py-5">
          <div className="flex-1">{step.component}</div>
        </div>
      </div>
    </>
  )
}
